import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  ButtonGroup,
  MenuItem,
  Select,
  FormControl,
  Alert,
} from "@mui/material";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import SearchBar from "../../components/forum/SearchBar";
import useIsMobile from "../../components/hooks/useIsMobile";
import {
  useAdminListAllAdvisors,
  useListAllFamilies,
} from "../../services/admin";
import { useUpdateAdvisor, useUpdateFamily } from "../../services/advisor";
import {
  Advisor,
  AdvisorStatus,
  EntityMetadata,
} from "../../protogen/common_pb";
import Loading from "../../components/common/Loading";
import {
  Family,
  FamilyStatus,
  UpdateAdvisorRequest,
  UpdateFamilyRequest,
} from "protogen/advisors_service_pb";
import {
  EditMetadataModal,
  FAMILY_ATTRIBUTION_FIELDS,
  FAMILY_DEMOGRAPHICS_FIELDS,
  fromProto,
  toProto,
} from "../../components/common/MetadataSection";
import LinkRouter from "components/navigation/LinkRouter";
import { useSearchParams } from "react-router-dom";

type FilterType = "all" | "needs_attribution" | "needs_demographic";
type EntityType = "advisors" | "members";
type ClickContext = "attribution" | "demographics" | "all";

interface MetadataItemProps {
  type: EntityType;
  item: Advisor | Family;
  missingFields: string[];
  onClick: (context: ClickContext) => void;
}

const MetadataItem = ({
  type,
  item,
  missingFields,
  onClick,
}: MetadataItemProps) => {
  const isMobile = useIsMobile();

  const getLink = () => {
    if (type === "advisors") {
      const advisor = item as Advisor;
      return `/tools/advisor-administration/${encodeURIComponent(advisor.ref)}`;
    } else {
      const family = item as Family;
      return `/families/${encodeURIComponent(family.ref)}`;
    }
  };

  const getName = () => {
    if (type === "advisors") {
      const advisor = item as Advisor;
      return `${advisor.firstName} ${advisor.lastName}`;
    } else {
      const family = item as Family;
      return family.name;
    }
  };

  const getEmail = () => {
    if (type === "advisors") {
      const advisor = item as Advisor;
      return advisor.personalEmail;
    } else {
      const family = item as Family;
      const primaryMember = family.familyMembers.find((m) => m.isPrimary);
      return primaryMember?.email || "";
    }
  };

  // Determine what buttons to show based on missing fields
  const getMissingCategories = () => {
    if (type === "advisors") {
      return {
        attribution: missingFields.includes("Channel"),
        demographics: false, // Advisors don't have demographics
      };
    } else {
      // For families
      return {
        attribution: missingFields.some((field) =>
          FAMILY_ATTRIBUTION_FIELDS.includes(field),
        ),
        demographics: missingFields.some((field) =>
          FAMILY_DEMOGRAPHICS_FIELDS.includes(field),
        ),
      };
    }
  };

  const { attribution, demographics } = getMissingCategories();

  return (
    <Box
      sx={{
        borderRadius: "8px",
        border: "1px solid #ECECEC",
        background: "#FFF",
        width: "100%",
        padding: "16px 24px",
        gap: "8px",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: "#198282",
          boxShadow: "0 2px 8px rgba(25, 130, 130, 0.1)",
        },
      }}
    >
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "flex-start" : "center"}
        gap="12px"
      >
        <Box flexGrow={1}>
          <Box display="flex" flexDirection="column" gap="4px">
            <LinkRouter to={getLink()}>
              <Typography variant="bodyHeavy" color="#262626">
                {getName()}
              </Typography>
            </LinkRouter>
            <Typography variant="body" color="#666">
              {getEmail()}
            </Typography>
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          gap="8px"
        >
          <Typography variant="bodySmall" color="#999">
            Missing: {missingFields.join(" • ")}
          </Typography>
          <Box display="flex" flexDirection="row" gap="8px" alignItems="center">
            {attribution && (
              <>
                <Button
                  variant="text"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick("attribution");
                  }}
                >
                  Add attribution
                </Button>
                {"•"}
              </>
            )}
            {demographics && (
              <>
                <Button
                  variant="text"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick("demographics");
                  }}
                >
                  Add demographics
                </Button>
                {"•"}
              </>
            )}
            <Button
              variant="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClick("all");
              }}
            >
              View metadata
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const hasMetadataField = (metadata: any, fieldName: string): boolean => {
  if (!metadata || !metadata.fields) return false;
  return metadata.fields.some(
    (field: any) => field.key === fieldName && field.value,
  );
};

const getAdvisorMissingFields = (advisor: Advisor): string[] => {
  const missing: string[] = [];

  if (!hasMetadataField(advisor.metadata, "channel")) {
    missing.push("Channel");
  }

  return missing;
};

const getFamilyMissingFields = (family: Family): string[] => {
  const missing: string[] = [];

  // Check attribution fields
  if (!hasMetadataField(family.metadata, "referrer")) {
    missing.push("Referrer");
  }
  if (!hasMetadataField(family.metadata, "channel")) {
    missing.push("Channel");
  }

  // Check demographic fields
  if (!hasMetadataField(family.metadata, "Is parent")) {
    missing.push("Is Parent");
  }
  if (!hasMetadataField(family.metadata, "Number of children")) {
    missing.push("Number of Children");
  }
  if (!hasMetadataField(family.metadata, "Home value")) {
    missing.push("Home Value");
  }
  if (!hasMetadataField(family.metadata, "Relationship status")) {
    missing.push("Relationship Status");
  }

  return missing;
};

const filterByType = (
  items: (Advisor | Family)[],
  entityType: EntityType,
  filterType: FilterType,
) => {
  return items.filter((item) => {
    const missingFields =
      entityType === "advisors"
        ? getAdvisorMissingFields(item as Advisor)
        : getFamilyMissingFields(item as Family);

    switch (filterType) {
      case "all":
        return true;
      case "needs_attribution":
        if (entityType === "advisors") {
          return missingFields.includes("Channel");
        } else {
          return missingFields.some((field) =>
            ["Referrer", "Channel"].includes(field),
          );
        }
      case "needs_demographic":
        if (entityType === "advisors") {
          return false; // Disabled for advisors
        } else {
          return missingFields.some((field) =>
            [
              "Is Parent",
              "Number of Children",
              "Home Value",
              "Relationship Status",
            ].includes(field),
          );
        }
      default:
        return true;
    }
  });
};

const stringMatch = (
  item: Advisor | Family,
  query: string,
  entityType: EntityType,
) => {
  const searchText = query.toLowerCase();

  if (entityType === "advisors") {
    const advisor = item as Advisor;
    return (
      advisor.firstName.toLowerCase().includes(searchText) ||
      advisor.lastName.toLowerCase().includes(searchText) ||
      advisor.personalEmail.toLowerCase().includes(searchText)
    );
  } else {
    const family = item as Family;
    const primaryMember = family.familyMembers.find((m) => m.isPrimary);
    return (
      family.name.toLowerCase().includes(searchText) ||
      (primaryMember?.email || "").toLowerCase().includes(searchText)
    );
  }
};

export default () => {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper to validate URL params
  const getValidEntityType = (param: string | null): EntityType => {
    if (param === "advisors" || param === "members") return param;
    return "advisors";
  };

  const getValidFilterType = (param: string | null): FilterType => {
    if (
      param === "all" ||
      param === "needs_attribution" ||
      param === "needs_demographic"
    )
      return param;
    return "all";
  };

  // Initialize state from URL params or defaults
  const [entityType, setEntityType] = useState<EntityType>(
    getValidEntityType(searchParams.get("type")),
  );
  const [filterType, setFilterType] = useState<FilterType>(
    getValidFilterType(searchParams.get("filter")),
  );
  const [query, setQuery] = useState<string>(searchParams.get("search") || "");
  const [selectedItem, setSelectedItem] = useState<Advisor | Family | null>(
    null,
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [clickContext, setClickContext] = useState<ClickContext>("all");

  const {
    request: requestAdvisors,
    data: advisorsData,
    loading: advisorsLoading,
  } = useAdminListAllAdvisors();
  const {
    request: requestFamilies,
    data: familiesData,
    loading: familiesLoading,
  } = useListAllFamilies();
  const { request: updateAdvisor } = useUpdateAdvisor();
  const { request: updateFamily } = useUpdateFamily();

  // Helper function to update both state and URL params
  const updateUrlParams = (
    updates: Partial<{
      type: EntityType;
      filter: FilterType;
      search: string;
    }>,
  ) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value && value !== "") {
            newParams.set(key, value);
          } else {
            newParams.delete(key);
          }
        });
        return newParams;
      },
      { replace: true },
    );
  };

  const handleEntityTypeChange = (newType: EntityType) => {
    setEntityType(newType);
    updateUrlParams({ type: newType });
  };

  const handleFilterTypeChange = (newFilter: FilterType) => {
    setFilterType(newFilter);
    updateUrlParams({ filter: newFilter });
  };

  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery);
    updateUrlParams({ search: newQuery });
  };

  const refresh = async () => {
    if (entityType === "advisors") {
      await requestAdvisors({});
    } else {
      await requestFamilies({});
    }
  };

  useEffect(() => {
    refresh();
  }, [entityType]);

  const handleItemClick = (item: Advisor | Family, context: ClickContext) => {
    setSelectedItem(item);
    setClickContext(context);
    setEditDialogOpen(true);
  };

  const getContextualMissingKeys = (
    item: Advisor | Family,
    context: ClickContext,
  ): string[] => {
    if (context === "all") {
      return []; // No pre-population for "View metadata"
    }

    const allMissingFields =
      entityType === "advisors"
        ? getAdvisorMissingFields(item as Advisor)
        : getFamilyMissingFields(item as Family);

    if (context === "attribution") {
      const attributionFields = ["Referrer", "Channel"];
      return allMissingFields
        .filter((field) => attributionFields.includes(field))
        .map((field) => {
          // Map display names to actual metadata keys
          const fieldMap: { [key: string]: string } = {
            Referrer: "referrer",
            Channel: "channel",
          };
          return fieldMap[field] || field.toLowerCase();
        });
    }

    if (context === "demographics") {
      const demographicsFields = [
        "Is Parent",
        "Number of Children",
        "Home Value",
        "Relationship Status",
      ];
      return allMissingFields
        .filter((field) => demographicsFields.includes(field))
        .map((field) => {
          // Map display names to actual metadata keys
          const fieldMap: { [key: string]: string } = {
            "Is Parent": "Is parent",
            "Number of Children": "Number of children",
            "Home Value": "Home value",
            "Relationship Status": "Relationship Status",
          };
          return fieldMap[field] || field;
        });
    }

    return [];
  };

  const handleSaveMetadata = async (metadata: { [key: string]: string }) => {
    if (!selectedItem) return;

    const protoMetadata = toProto(metadata);

    if (entityType === "advisors") {
      const advisor = selectedItem as Advisor;
      await updateAdvisor(
        new UpdateAdvisorRequest({
          advisorRef: advisor.ref,
          shouldUpdateMetadata: true,
          updatedMetadata: protoMetadata as EntityMetadata,
        }),
      );
    } else {
      const family = selectedItem as Family;
      await updateFamily(
        new UpdateFamilyRequest({
          familyRef: family.ref,
          shouldUpdateMetadata: true,
          updatedMetadata: protoMetadata as EntityMetadata,
        }),
      );
    }

    refresh();
    setEditDialogOpen(false);
  };
  const SKIP_ADVISOR_STATUS = [AdvisorStatus.TEST, AdvisorStatus.STAFF];
  const SKIP_FAMILY_STATUS = [
    FamilyStatus.TEST,
    FamilyStatus.DEMO,
    FamilyStatus.TEST_HIDDEN,
  ];
  const currentData =
    entityType === "advisors"
      ? advisorsData?.advisors.filter(
          (a) => !SKIP_ADVISOR_STATUS.includes(a.status),
        ) || []
      : familiesData?.families.filter(
          (f) => !SKIP_FAMILY_STATUS.includes(f.status),
        ) || [];

  const loading = entityType === "advisors" ? advisorsLoading : familiesLoading;

  let filteredItems = filterByType(currentData, entityType, filterType);

  if (query) {
    filteredItems = filteredItems.filter((item) =>
      stringMatch(item, query, entityType),
    );
  }

  const itemsWithMissingFields = filteredItems
    .map((item) => {
      const missingFields =
        entityType === "advisors"
          ? getAdvisorMissingFields(item as Advisor)
          : getFamilyMissingFields(item as Family);
      return { item, missingFields };
    })
    .filter(({ missingFields }) => {
      // Only show items that actually have missing fields for the selected filter
      if (filterType === "all") return missingFields.length > 0;
      return missingFields.length > 0;
    });

  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px 20px 0px 20px" : "64px 64px 0 64px",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Breadcrumbs
            breadcrumbs={[
              { name: "Home", link: "/" },
              { name: "Tools", link: "/tools" },
            ]}
          />
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Typography variant="display">Pipeline Metadata</Typography>
            {!isMobile && (
              <SearchBar
                initialQuery={query}
                fullWidth={false}
                autofocus={false}
                onQuery={handleQueryChange}
                onKeyPress={true}
                sx={{ minWidth: "300px" }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: isMobile ? "flex-start" : "flex-end",
          gap: isMobile ? "12px" : "24px",
          marginBottom: "16px",
        }}
      >
        {isMobile && (
          <SearchBar
            initialQuery={query}
            fullWidth={true}
            autofocus={true}
            onQuery={handleQueryChange}
            onKeyPress={true}
            sx={{ marginLeft: "0" }}
          />
        )}

        <Box
          display="flex"
          flexDirection="row"
          gap="12px"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <ButtonGroup
            style={{
              display: "flex",
              ...(isMobile && {
                width: "100%",
              }),
            }}
          >
            <Button
              sx={{ flex: "1", width: "100%" }}
              variant={
                entityType === "advisors"
                  ? "buttonGroupSelected"
                  : "buttonGroup"
              }
              onClick={() => handleEntityTypeChange("advisors")}
            >
              Advisors
            </Button>
            <Button
              sx={{ flex: "1", width: "100%" }}
              variant={
                entityType === "members" ? "buttonGroupSelected" : "buttonGroup"
              }
              onClick={() => handleEntityTypeChange("members")}
            >
              Members
            </Button>
          </ButtonGroup>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={filterType}
              onChange={(e) =>
                handleFilterTypeChange(e.target.value as FilterType)
              }
              variant="outlined"
              sx={{
                minHeight: "42.25px",
                ".MuiSelect-select": {
                  marginTop: "0",
                },
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="needs_attribution">Needs Attribution</MenuItem>
              <MenuItem
                value="needs_demographic"
                disabled={entityType === "advisors"}
              >
                Needs Demographic
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "16px",
        }}
      >
        {loading && <Loading />}

        {!itemsWithMissingFields.length && !loading && (
          <Box>
            <Alert severity="info">No items found with missing metadata</Alert>
          </Box>
        )}

        {!loading && itemsWithMissingFields.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {itemsWithMissingFields.map(({ item, missingFields }, index) => (
              <MetadataItem
                key={`${entityType}-${item.ref || index}`}
                type={entityType}
                item={item}
                missingFields={missingFields}
                onClick={(context) => handleItemClick(item, context)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Metadata Editing Modal */}
      {selectedItem && editDialogOpen && (
        <EditMetadataModal
          metadata={fromProto(selectedItem.metadata || { fields: [] })}
          handleSave={handleSaveMetadata}
          open={editDialogOpen}
          handleClose={() => setEditDialogOpen(false)}
          entityType={entityType === "advisors" ? "advisor" : "family"}
          missingKeys={getContextualMissingKeys(selectedItem, clickContext)}
        />
      )}
    </Box>
  );
};
